import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, Users, FileEdit, BookOpen, LogOut, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: BarChart3 },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/content', label: 'Content', icon: FileEdit },
];

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-panel">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-[#111] border-b border-[#2a2a2a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="flex items-center gap-2 group">
              <div className="p-1.5 bg-[#F5C518]/10 rounded-lg">
                <BookOpen className="w-4 h-4 text-[#F5C518]" />
              </div>
              <span className="text-sm font-semibold text-white">Untangling AI</span>
              <span className="text-xs font-medium px-2 py-0.5 bg-[#F5C518]/10 text-[#F5C518] rounded-full border border-[#F5C518]/20">Admin</span>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="px-3 py-1.5 text-xs font-medium text-[#F5C518] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 border border-[#F5C518]/20 rounded-lg transition-colors" data-testid="reader-view-button">
              <BookOpen className="w-3.5 h-3.5 inline mr-1" />Reader View
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/10 transition-colors text-sm">
                  <div className="w-6 h-6 rounded-full bg-[#F5C518]/20 flex items-center justify-center">
                    <span className="text-[#F5C518] text-xs font-bold">{user?.email?.[0]?.toUpperCase()}</span>
                  </div>
                  <ChevronDown className="w-3 h-3 text-[#888]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-gray-200">
                <div className="px-3 py-2 border-b border-gray-100"><p className="text-xs text-gray-500 truncate">{user?.email}</p></div>
                <DropdownMenuItem onClick={logout} className="text-red-500 hover:bg-gray-50 cursor-pointer"><LogOut className="w-4 h-4 mr-2" />Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Navigation tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 -mb-px">
            {NAV_ITEMS.map(item => (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${isActive(item.path) ? 'border-[#F5C518] text-[#F5C518]' : 'border-transparent text-[#888] hover:text-white hover:border-[#444]'}`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />{item.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
